export type PlaybackTrigger =
  | 'video-ready'
  | 'canplay'
  | 'wechat-bridge'
  | 'wechat-network-callback'
  | 'visibility'
  | 'user-interaction'
  | 'recovery'
  | 'viewport'
export type VideoDiagnosticLevel = 'success' | 'error' | 'info'

export interface VideoDiagnosticEntry {
  id: number
  timestamp: number
  level: VideoDiagnosticLevel
  trigger: PlaybackTrigger
  name: string
  message: string
  diagnosis?: string
}

type VideoDiagnosticListener = (entries: readonly VideoDiagnosticEntry[]) => void

const MAX_ENTRIES = 80
const entries: VideoDiagnosticEntry[] = []
const listeners = new Set<VideoDiagnosticListener>()
let nextId = 1

const notify = () => {
  const snapshot = [...entries]
  listeners.forEach((listener) => listener(snapshot))
}

const append = (entry: Omit<VideoDiagnosticEntry, 'id' | 'timestamp'>) => {
  entries.push({ ...entry, id: nextId++, timestamp: Date.now() })
  if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES)
  notify()
}

const readErrorField = (error: unknown, field: 'name' | 'message', fallback: string) => {
  if (typeof error !== 'object' || error === null || !(field in error)) return fallback
  const value = (error as Record<'name' | 'message', unknown>)[field]
  return typeof value === 'string' && value ? value : fallback
}

export const logVideoPlaybackInfo = (trigger: PlaybackTrigger, message: string) => {
  console.log('[RARECARD video]', trigger, message)
  append({ level: 'info', trigger, name: 'info', message })
}

export const logVideoPlaybackSuccess = (trigger: PlaybackTrigger) => {
  console.log('[RARECARD video]', trigger, 'play ok')
  append({ level: 'success', trigger, name: 'play ok', message: 'video.play() resolved' })
}

export const logVideoPlaybackError = (trigger: PlaybackTrigger, error: unknown) => {
  const name = readErrorField(error, 'name', 'UnknownError')
  const message = readErrorField(error, 'message', String(error))
  let diagnosis: string | undefined

  if (name === 'NotAllowedError') {
    diagnosis = '自动播放被授权或浏览器策略阻止'
  } else if (name === 'NotSupportedError') {
    diagnosis = '媒体格式、编码或当前解码器不支持'
  }

  // 控制台继续保留浏览器返回的原始 name 和 message。
  console.log('[RARECARD video]', trigger, name, message)
  if (name === 'NotAllowedError') {
    console.log('[RARECARD video] diagnosis: autoplay permission/policy blocked')
  } else if (name === 'NotSupportedError') {
    console.log('[RARECARD video] diagnosis: media format/codec/decoder unsupported')
  }

  append({ level: 'error', trigger, name, message, diagnosis })
  return { name, message, diagnosis }
}

export const subscribeVideoDiagnostics = (listener: VideoDiagnosticListener) => {
  listeners.add(listener)
  listener([...entries])
  return () => listeners.delete(listener)
}

export const clearVideoDiagnostics = () => {
  entries.splice(0, entries.length)
  notify()
}
